// This file is automatically generated. Do not edit it directly.
import { createClient, SupabaseClientOptions } from '@supabase/supabase-js';
import type { Database } from './types';

// These values are public keys intended for client-side use, but we're still using constants
// to make them easier to manage and to follow security best practices
const SUPABASE_URL = "https://xyflmiuzahuywrmpvogq.supabase.co";
const SUPABASE_PUBLISHABLE_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Inh5ZmxtaXV6YWh1eXdybXB2b2dxIiwicm9sZSI6ImFub24iLCJpYXQiOjE3MzkyODM2NTYsImV4cCI6MjA1NDg1OTY1Nn0.rKwwyqWqatojkg6HYAzl43eMtfYXve2CFk2Kk9eHy9A";

// Import the supabase client like this:
// import { supabase } from "@/integrations/supabase/client";

// Implementação de armazenamento simplificado para melhor compatibilidade mobile
const mobileOptimizedStorage = {
  getItem: (key: string): string | null => {
    try {
      // Primeira tentativa: localStorage (mais confiável)
      const item = localStorage.getItem(key);
      if (item) {
        // Verificar se o token não está corrompido
        try {
          JSON.parse(item);
          return item;
        } catch {
          // Token corrompido, limpar e tentar sessionStorage
          localStorage.removeItem(key);
        }
      }
      
      // Segunda tentativa: sessionStorage
      const sessionItem = sessionStorage.getItem(key);
      if (sessionItem) {
        try {
          JSON.parse(sessionItem);
          // Se sessionStorage funciona, sincronizar com localStorage
          localStorage.setItem(key, sessionItem);
          return sessionItem;
        } catch {
          sessionStorage.removeItem(key);
        }
      }
      
      return null;
    } catch (error) {
      console.warn('Erro ao recuperar token:', error);
      return null;
    }
  },
  
  setItem: (key: string, value: string): void => {
    try {
      // Validar o valor antes de armazenar
      JSON.parse(value);
      
      // Gravar primeiro no localStorage
      localStorage.setItem(key, value);
      
      // Backup no sessionStorage apenas se localStorage falhar
      try {
        const test = localStorage.getItem(key);
        if (!test) {
          sessionStorage.setItem(key, value);
        }
      } catch {
        sessionStorage.setItem(key, value);
      }
    } catch (error) {
      console.warn('Erro ao salvar token:', error);
      // Em caso de erro, tentar apenas sessionStorage
      try {
        sessionStorage.setItem(key, value);
      } catch (sessionError) {
        console.error('Falha crítica no armazenamento:', sessionError);
      }
    }
  },
  
  removeItem: (key: string): void => {
    try {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    } catch (error) {
      console.warn('Erro ao remover token:', error);
    }
  }
};

// Opções de cliente otimizadas para dispositivos móveis
const clientOptions: SupabaseClientOptions<'public'> = {
  auth: {
    storage: mobileOptimizedStorage,
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    flowType: 'pkce',
    // Configurações específicas para mobile
    debug: process.env.NODE_ENV === 'development',
    storageKey: 'sb-auth-token',
  },
  db: {
    schema: 'public',
  },
  global: {
    headers: {
      'x-custom-app': 'broker-base-dashboard',
    },
  },
  realtime: {
    reconnectAfterMs: attempt => Math.min(1000 * Math.pow(2, attempt), 10000),
  },
};

export const supabase = createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, clientOptions);

// Minimized logging in production to avoid leaking sensitive information
if (process.env.NODE_ENV === 'development') {
  console.log('Supabase client initialized with URL:', SUPABASE_URL);
  console.log('Current Supabase client configuration:', {
    url: SUPABASE_URL,
    key: SUPABASE_PUBLISHABLE_KEY ? 'Valid key provided' : 'No key provided',
    auth: supabase.auth ? 'Auth module loaded' : 'Auth module not loaded',
    tableName: 'db_imoveis_vendas', // Alterado para 'db_imoveis_vendas'
  });
}

// Add rate limiting to the Supabase data fetching
const rateLimitedFetch = (() => {
  // Increased limits based on database size (36,000+ records) and pagination of 2000 records
  // Considering multiple concurrent users and the need to fetch multiple pages
  const MAX_REQUESTS_PER_MINUTE = 250; // Increased from 50 to 250
  const BURST_LIMIT = 25; // Allow bursts of requests
  let requestCount = 0;
  let lastResetTime = Date.now();
  let burstTokens = BURST_LIMIT;
  const burstRefillInterval = 5000; // Refill burst tokens every 5 seconds
  
  // Setup burst token refill
  setInterval(() => {
    if (burstTokens < BURST_LIMIT) {
      burstTokens = Math.min(burstTokens + 1, BURST_LIMIT);
    }
  }, burstRefillInterval);
  
  return async (url: string, options: RequestInit) => {
    const now = Date.now();
    if (now - lastResetTime > 60000) {
      requestCount = 0;
      lastResetTime = now;
    }
    
    // Use burst tokens for spike in requests, but maintain overall rate limit
    if (requestCount >= MAX_REQUESTS_PER_MINUTE) {
      if (burstTokens <= 0) {
        throw new Error('Rate limit exceeded. Please try again later.');
      }
      burstTokens--;
    }
    
    requestCount++;
    return fetch(url, options);
  };
})();

// Cache para datas máximas
const maxScrapeDateCache: Record<string, string> = {};

// Função para obter a data máxima
export const getMaxScrapeDate = async (tableName: string): Promise<string | null> => {
  try {
    const { data, error } = await supabase.rpc('get_max_scrape_date', {
      table_name: tableName
    });

    if (error) {
      console.error('Error fetching max scrape date:', error);
      return null;
    }

    return data;
  } catch (e) {
    console.error('Error in getMaxScrapeDate:', e);
    return null;
  }
};

// Função otimizada para buscar dados com cache de data
export const fetchTableDataWithCache = async (
  tableName: string,
  offset: number = 0,
  limit: number = 2000
): Promise<{ data: any[]; error: any; message?: string }> => {
  try {
    // Usa data em cache ou busca nova
    if (!maxScrapeDateCache[tableName]) {
      const maxDate = await getMaxScrapeDate(tableName);
      if (maxDate) {
        maxScrapeDateCache[tableName] = maxDate;
      }
    }

    const response = await rateLimitedFetch(`${SUPABASE_URL}/rest/v1/rpc/get_table_data`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': SUPABASE_PUBLISHABLE_KEY,
        'Authorization': `Bearer ${SUPABASE_PUBLISHABLE_KEY}`,
      },
      body: JSON.stringify({
        table_name: tableName,
        limit_count: Number(limit),
        offset_value: Number(offset),
        max_scrape_date: maxScrapeDateCache[tableName]
      })
    });

    if (!response.ok) {
      console.error('Fetch failed:', await response.text());
      // Limpa o cache em caso de erro
      delete maxScrapeDateCache[tableName];
      return { 
        data: [], 
        error: new Error('Failed to fetch data'),
        message: 'Erro ao buscar dados'
      };
    }

    const data = await response.json();
    return { data, error: null };
  } catch (e) {
    console.error('Error in fetchTableDataWithCache:', e);
    // Limpa o cache em caso de erro
    delete maxScrapeDateCache[tableName];
    return { 
      data: [], 
      error: e,
      message: 'Erro ao buscar dados'
    };
  }
};

// Versão otimizada da função fetchAllImoveisData
export const fetchAllImoveisData = async () => {
  try {
    console.log('Fetching all records from db_imoveis_vendas table using optimized RPC pagination...');
    const allData = [];
    let page = 1;
    const pageSize = 2000;
    let hasMore = true;
    let consecutiveErrors = 0;
    const MAX_CONSECUTIVE_ERRORS = 3;
    const MAX_PAGES = 100;

    while (hasMore && page <= MAX_PAGES) {
      console.log(`Fetching page ${page} with offset ${(page - 1) * pageSize}`);
      
      const result = await fetchTableDataWithCache(
        'db_imoveis_vendas',
        (page - 1) * pageSize,
        pageSize
      );

      if (result.error) {
        consecutiveErrors++;
        if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
          throw new Error(`Failed after ${MAX_CONSECUTIVE_ERRORS} consecutive errors`);
        }
        const backoffTime = 1000 * Math.pow(2, consecutiveErrors) + Math.random() * 1000;
        console.log(`Backing off for ${backoffTime}ms before retrying`);
        await new Promise(resolve => setTimeout(resolve, backoffTime));
        continue;
      }

      consecutiveErrors = 0;
      const data = result.data;
      console.log(`Page ${page} returned ${data.length} records`);

      if (data.length > 0) {
        allData.push(...data);
        page++;
      }

      hasMore = data.length === pageSize;
    }

    return { data: allData, error: null, message: 'Data fetched successfully' };
  } catch (e) {
    console.error('Error in fetchAllImoveisData:', e);
    return { 
      data: [], 
      error: e, 
      message: 'Error fetching data' 
    };
  }
};

// Versão otimizada da função fetchAllImoveisAluguelData
export const fetchAllImoveisAluguelData = async () => {
  try {
    console.log('Fetching all records from db_imoveis_aluguel table using optimized RPC pagination...');
    const allData = [];
    let page = 1;
    const pageSize = 2000;
    let hasMore = true;
    let consecutiveErrors = 0;
    const MAX_CONSECUTIVE_ERRORS = 3;
    const MAX_PAGES = 100;

    while (hasMore && page <= MAX_PAGES) {
      console.log(`Fetching page ${page} with offset ${(page - 1) * pageSize}`);
      
      const result = await fetchTableDataWithCache(
        'db_imoveis_aluguel',
        (page - 1) * pageSize,
        pageSize
      );

      if (result.error) {
        consecutiveErrors++;
        if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
          throw new Error(`Failed after ${MAX_CONSECUTIVE_ERRORS} consecutive errors`);
        }
        const backoffTime = 1000 * Math.pow(2, consecutiveErrors) + Math.random() * 1000;
        console.log(`Backing off for ${backoffTime}ms before retrying`);
        await new Promise(resolve => setTimeout(resolve, backoffTime));
        continue;
      }

      consecutiveErrors = 0;
      const data = result.data;
      console.log(`Page ${page} returned ${data.length} records`);

      if (data.length > 0) {
        allData.push(...data);
        page++;
      }

      hasMore = data.length === pageSize;
    }

    return { data: allData, error: null, message: 'Data fetched successfully' };
  } catch (e) {
    console.error('Error in fetchAllImoveisAluguelData:', e);
    return { 
      data: [], 
      error: e, 
      message: 'Error fetching data' 
    };
  }
};

// Original test function, kept for compatibility
export const testFetchDbImoveis = async () => {
  try {
    console.log('Testing db_imoveis_vendas table access...');
    
    // First, let's try a raw query to just check if the table exists and is accessible
    const { data: rawData, error: rawError } = await supabase.rpc('get_price_stats');
    
    console.log('Raw query for price stats result:', { rawData, rawError });
    
    // Use the new function to fetch all data
    const allDataResult = await fetchAllImoveisData();
    if (allDataResult.data && allDataResult.data.length > 0) {
      return allDataResult;
    }
    
    // If the new method fails, try a direct approach with a very high limit
    try {
      const response = await fetch(`${SUPABASE_URL}/rest/v1/rpc/get_table_data`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_PUBLISHABLE_KEY,
          'Authorization': `Bearer ${SUPABASE_PUBLISHABLE_KEY}`,
        },
        body: JSON.stringify({
          table_name: 'db_imoveis_vendas',
          limit_count: Number(1000000), // Request a very high limit
          offset_value: Number(0)
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Direct fetch to get_table_data RPC result found', data.length, 'records');
        
        if (data && data.length > 0) {
          return { 
            data, 
            error: null, 
            message: 'Data fetched successfully with direct RPC call' 
          };
        }
      } else {
        console.error('Error with direct RPC call:', await response.text());
      }
    } catch (directError) {
      console.error('Exception with direct RPC call:', directError);
    }
    
    // Now check if the table exists through a SELECT query
    const { data: tableExists, error: tableError } = await supabase
      .from('db_imoveis_vendas')
      .select('id')
      .limit(1);
      
    if (tableError) {
      console.error('Error accessing db_imoveis_vendas table:', tableError);
      return { data: [], error: tableError, message: 'Error accessing table' };
    }
    
    console.log('Table access successful, now fetching data...');
    
    // Now let's fetch actual data directly
    const { data, error } = await supabase
      .from('db_imoveis_vendas')
      .select('*');
    
    console.log('Full Supabase response:', { dataLength: data?.length, error });
    
    if (error) {
      console.error('Error fetching data from db_imoveis_vendas:', error);
      return { data: [], error, message: 'Error fetching data' };
    }
    
    if (!data || data.length === 0) {
      console.warn('No data found in db_imoveis_vendas table');
      return { data: [], error: null, message: 'No data found' };
    }
    
    console.log('db_imoveis_vendas test query result:', {
      success: !error, 
      count: data.length,
      firstItem: data[0],
      columns: data[0] ? Object.keys(data[0]) : []
    });
    
    return { data, error: null, message: 'Data fetched successfully' };
  } catch (e) {
    console.error('Unexpected error testing db_imoveis_vendas table access:', e);
    return { data: [], error: e, message: 'Unexpected error' };
  }
};

// New test function for rental properties
export const testFetchDbImoveisAluguel = async () => {
  try {
    console.log('Testing db_imoveis_aluguel table access...');
    
    // First, let's try a raw query to just check if the table exists and is accessible
    const { data: rawData, error: rawError } = await supabase.rpc('get_price_stats');
    
    console.log('Raw query for price stats result:', { rawData, rawError });
    
    // Use the new function to fetch all data
    const allDataResult = await fetchAllImoveisAluguelData();
    if (allDataResult.data && allDataResult.data.length > 0) {
      return allDataResult;
    }
    
    // If the new method fails, try a direct approach with a very high limit
    try {
      const response = await fetch(`${SUPABASE_URL}/rest/v1/rpc/get_table_data`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_PUBLISHABLE_KEY,
          'Authorization': `Bearer ${SUPABASE_PUBLISHABLE_KEY}`,
        },
        body: JSON.stringify({
          table_name: 'db_imoveis_aluguel',
          limit_count: Number(1000000), // Request a very high limit
          offset_value: Number(0)
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Direct fetch to get_table_data RPC result found', data.length, 'records');
        
        if (data && data.length > 0) {
          return { 
            data, 
            error: null, 
            message: 'Data fetched successfully with direct RPC call' 
          };
        }
      } else {
        console.error('Error with direct RPC call:', await response.text());
      }
    } catch (directError) {
      console.error('Exception with direct RPC call:', directError);
    }
    
    // Now check if the table exists through a SELECT query
    const { data: tableExists, error: tableError } = await supabase
      .from('db_imoveis_aluguel')
      .select('id')
      .limit(1);
      
    if (tableError) {
      console.error('Error accessing db_imoveis_aluguel table:', tableError);
      return { data: [], error: tableError, message: 'Error accessing table' };
    }
    
    console.log('Table access successful, now fetching data...');
    
    // Now let's fetch actual data directly
    const { data, error } = await supabase
      .from('db_imoveis_aluguel')
      .select('*');
    
    console.log('Full Supabase response:', { dataLength: data?.length, error });
    
    if (error) {
      console.error('Error fetching data from db_imoveis_aluguel:', error);
      return { data: [], error, message: 'Error fetching data' };
    }
    
    if (!data || data.length === 0) {
      console.warn('No data found in db_imoveis_aluguel table');
      return { data: [], error: null, message: 'No data found' };
    }
    
    console.log('db_imoveis_aluguel test query result:', {
      success: !error, 
      count: data.length,
      firstItem: data[0],
      columns: data[0] ? Object.keys(data[0]) : []
    });
    
    return { data, error: null, message: 'Data fetched successfully' };
  } catch (e) {
    console.error('Unexpected error testing db_imoveis_aluguel table access:', e);
    return { data: [], error: e, message: 'Unexpected error' };
  }
};

// Do not run the immediate test in production to avoid unnecessary API calls
if (process.env.NODE_ENV === 'development') {
  // Immediate test on load to check connection
  testFetchDbImoveis().then(result => {
    console.log('Initial test fetch result:', {
      message: result.message,
      success: !result.error,
      dataCount: result.data.length,
    });
  }).catch(err => {
    console.error('Error running initial test fetch:', err);
  });
}

export const fetchEntraramUltimaSemanaData = async () => {
  try {
    const { data, error } = await supabase.rpc('entraram_ultima_semana');
    
    if (error) {
      console.error('Error fetching entraram_ultima_semana data:', error.message);
      return { data: null, error, message: 'Erro ao buscar dados de imóveis que entraram na última semana' };
    }

    if (!data) {
      return { data: [], error: null, message: 'Nenhum imóvel encontrado' };
    }

    return { data, error: null, message: null };
  } catch (e: any) {
    console.error('Error in fetchEntraramUltimaSemanaData:', e?.message);
    return { 
      data: null, 
      error: { message: e?.message || 'Erro desconhecido' }, 
      message: 'Erro ao buscar dados de imóveis que entraram na última semana' 
    };
  }
};

export const fetchSairamUltimaSemanaData = async () => {
  try {
    const { data, error } = await supabase.rpc('sairam_ultima_semana');
    
    if (error) {
      console.error('Error fetching sairam_ultima_semana data:', error.message);
      return { data: null, error, message: 'Erro ao buscar dados de imóveis que sairam na última semana' };
    }

    if (!data) {
      return { data: [], error: null, message: 'Nenhum imóvel encontrado' };
    }

    return { data, error: null, message: null };
  } catch (e: any) {
    console.error('Error in fetchSairamUltimaSemanaData:', e?.message);
    return { 
      data: null, 
      error: { message: e?.message || 'Erro desconhecido' }, 
      message: 'Erro ao buscar dados de imóveis que sairam na última semana' 
    };
  }
};


